type ApiErrorLike = {
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
  status?: number;
  data?: unknown;
};

type ApiErrorMessageOptions = {
  tooManyAttemptsMessage?: string;
  conflictMessage?: string;
  sessionExpiredMessage?: string;
  serverErrorMessage?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function firstDetailMessage(details: unknown): string | undefined {
  if (!isRecord(details)) {
    return undefined;
  }

  for (const value of Object.values(details)) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }

    if (Array.isArray(value)) {
      const first = value.find(
        (entry): entry is string =>
          typeof entry === 'string' && entry.trim().length > 0
      );
      if (first) {
        return first.trim();
      }
    }

    if (isRecord(value)) {
      const nested = firstDetailMessage(value);
      if (nested) {
        return nested;
      }
    }
  }

  return undefined;
}

function responsePayload(error: ApiErrorLike): Record<string, unknown> | undefined {
  const payload = error.response?.data ?? error.data;
  return isRecord(payload) ? payload : undefined;
}

export function getApiErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
  options: ApiErrorMessageOptions = {}
): string {
  const apiError = isRecord(error) ? (error as ApiErrorLike) : undefined;
  if (!apiError) {
    return fallback;
  }

  const payload = responsePayload(apiError);
  const payloadMessage =
    typeof payload?.message === 'string' ? payload.message.trim() : '';
  const detailMessage = firstDetailMessage(payload?.details);

  const isFieldValidation = payload?.code === 'invalid_partner_registration_data' ||
    payload?.code === 'invalid_registration_data';
  if (isFieldValidation && detailMessage) return detailMessage;

  if (payloadMessage && payloadMessage !== 'Validation error') {
    return payloadMessage;
  }

  if (detailMessage) {
    return detailMessage;
  }

  const payloadError =
    typeof payload?.error === 'string' ? payload.error.trim() : '';
  if (payloadError && !payloadError.includes('status code')) {
    return payloadError;
  }

  const payloadDetail =
    typeof payload?.detail === 'string' ? payload.detail.trim() : '';
  if (payloadDetail && !payloadDetail.includes('status code')) {
    return payloadDetail;
  }

  const status = apiError.response?.status ?? apiError.status;
  if (status === 429) {
    return options.tooManyAttemptsMessage || 'Too many attempts. Please wait a moment and try again.';
  }
  if (status === 409) {
    return options.conflictMessage || 'An account with this phone number already exists. Please sign in instead.';
  }
  if (status === 401) {
    return options.sessionExpiredMessage || 'Your session has expired. Please start again.';
  }
  if (typeof status === 'number' && status >= 500) {
    return options.serverErrorMessage || 'Something went wrong on our end. Please try again later.';
  }

  const message = typeof apiError.message === 'string' ? apiError.message : '';
  if (message.includes('Network Error') || message.toLowerCase().includes('network')) {
    return 'Please check your internet connection and try again.';
  }
  if (message && !message.includes('status code')) {
    return message;
  }

  return fallback;
}
